import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

export const RoundGroupedMatches = ({ matches, championshipId }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Group matches by round
  const matchesByRound = matches.reduce((acc, match) => {
    if (!acc[match.round]) {
      acc[match.round] = [];
    }
    acc[match.round].push(match);
    return acc;
  }, {});

  // Sort rounds numerically
  const sortedRounds = Object.keys(matchesByRound).sort((a, b) => parseInt(a) - parseInt(b));

  const handleMatchClick = (match) => {
    navigate(`/championship/${championshipId}/partita/${match.id}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Extract just the date part (e.g., "31.01" from "Dom 31/01/2025")
    const match = dateStr.match(/(\d{2})\/(\d{2})/);
    if (match) {
      return `${match[1]}.${match[2]}`;
    }
    return dateStr;
  };

  // Show message if no matches
  if (!matches || matches.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-gray-100 dark:bg-gray-800">
          <CalendarIcon className="w-8 h-8 text-gray-400 dark:text-gray-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Nessuna partita disponibile
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          I dati per questo campionato non sono ancora stati caricati.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-500">
          Vai al <a href="/admin" className="text-blue-600 dark:text-blue-400 hover:underline">Pannello Admin</a> per aggiornare i dati del campionato.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedRounds.map(round => (
        <div key={round} className="overflow-hidden">
          {/* Round Header - Blue with Calendar Icon */}
          <div className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 flex items-center space-x-3 rounded-t-xl">
            <div className="bg-yellow-400 p-2 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-blue-900" />
            </div>
            <h3 className="text-lg font-bold">
              Giornata {round}
            </h3>
          </div>

          {/* Matches Cards */}
          <div className="bg-white dark:bg-gray-800 rounded-b-xl border border-gray-200 dark:border-gray-700 border-t-0">
            {matchesByRound[round].map((match, index) => (
              <div
                key={match.id}
                onClick={() => handleMatchClick(match)}
                className={`
                  px-2 sm:px-4 py-3 cursor-pointer
                  hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                  ${index !== matchesByRound[round].length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}
                `}
              >
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  {/* Home Team */}
                  <div className="flex-1 flex items-center justify-end space-x-3 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white text-right truncate">
                      {match.homeTeam}
                    </span>
                    {match.played && match.homeScore !== null && (
                      <span className="text-base font-bold text-gray-900 dark:text-white min-w-[20px] text-center flex-shrink-0">
                        {match.homeScore}
                      </span>
                    )}
                  </div>

                  {/* Date and Time in Center */}
                  <div className="flex-shrink-0 mx-4 text-center min-w-[70px]">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatDate(match.date)}
                    </div>
                    {match.time && (
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {match.time}
                      </div>
                    )}
                  </div>

                  {/* Away Team */}
                  <div className="flex-1 flex items-center space-x-3 min-w-0">
                    {match.played && match.awayScore !== null && (
                      <span className="text-base font-bold text-gray-900 dark:text-white min-w-[20px] text-center flex-shrink-0">
                        {match.awayScore}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {match.awayTeam}
                    </span>
                  </div>
                </div>

                {/* Mobile Layout - Stack vertically */}
                <div className="sm:hidden space-y-2">
                  {/* Date and Time */}
                  <div className="text-center">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDate(match.date)}
                    </span>
                    {match.time && (
                      <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">
                        {match.time}
                      </span>
                    )}
                  </div>

                  {/* Teams */}
                  <div className="space-y-1">
                    {/* Home Team */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-900 dark:text-white truncate flex-1 mr-2">
                        {match.homeTeam}
                      </span>
                      {match.played && match.homeScore !== null && (
                        <span className="text-sm font-bold text-gray-900 dark:text-white flex-shrink-0">
                          {match.homeScore}
                        </span>
                      )}
                    </div>

                    {/* Away Team */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-900 dark:text-white truncate flex-1 mr-2">
                        {match.awayTeam}
                      </span>
                      {match.played && match.awayScore !== null && (
                        <span className="text-sm font-bold text-gray-900 dark:text-white flex-shrink-0">
                          {match.awayScore}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
